import { NavLink, useNavigate, useLocation } from "react-router-dom";
import HeaderLogo from "./HeaderLogo";
import DeferredGoogleSearch from "./DeferredGoogleSearch";
import {
  lazy,
  Suspense,
  useState,
  useEffect,
  useContext,
  useRef,
} from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Avatar,
  Tooltip,
  Typography,
  Divider,
  Box,
  Badge,
  Switch,
  Snackbar,
  Alert,
} from "@mui/material";
import Logout from "@mui/icons-material/Logout";
import Settings from "@mui/icons-material/Settings";
import Dashboard from "@mui/icons-material/Dashboard";
import NotificationsIcon from "@mui/icons-material/Notifications";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import ForumIcon from "@mui/icons-material/Forum";
import MessageIcon from "@mui/icons-material/Message";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import NightlightIcon from "@mui/icons-material/Nightlight";
import TranslateIcon from "@mui/icons-material/Translate";
import { useTranslation } from "react-i18next";
import { getExclusiveBlogLanguage } from "../pages/blog/blogCatalog";

import useUserRole from "../hooks/useUserRole";
import { AuthContext } from "../utils/AuthContext";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SecurityIcon from "@mui/icons-material/Security";

import {
  applyTheme,
  getInitialTheme,
  saveThemePreference,
  getThemeScopeForPath,
} from "../utils/theme";
import "../styles/Header.css";

const GLOBAL_MESSAGE_SOUND = "/sounds/message.mp3";


const getActiveChatId = (pathname = "") => {
  const match = String(pathname).match(
    /^\/messages\/([^/?#]+)/
  );

  return match ? match[1] : null;
};

const AuthModal = lazy(() => import("./AuthModal"));

const MyApplicationsModal = lazy(() =>
  import("./MyApplicationsModal")
);

const ProfileModal = lazy(() =>
  import("./ProfileModal")
);

const BookmarksModal = lazy(() =>
  import("./BookmarksModal")
);

const ForumModal = lazy(() =>
  import("./ForumModal")
);

const NotificationsModal = lazy(() =>
  import("./NotificationsModal")
);

const Header = () => {
  const { t, i18n } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [showAuthModal, setShowAuthModal] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [langAnchor, setLangAnchor] = useState(null);
  const themeScope = getThemeScopeForPath(
    location.pathname
  );

  const [darkMode, setDarkMode] = useState(
    () => getInitialTheme(themeScope) === "dark"
  );
  const [showMyApps, setShowMyApps] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showForum, setShowForum] = useState(false);
  const [msgCount, setMsgCount] = useState(0);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [mapNoticeOpen, setMapNoticeOpen] = useState(false);

  const { role } = useUserRole();

  const [notifCount, setNotifCount] = useState(0);

  /*
   * Короткие одноразовые анимации при появлении
   * новых уведомлений и сообщений.
   */
  const [notifPulse, setNotifPulse] = useState(false);
  const [messagePulse, setMessagePulse] = useState(false);

  const previousNotifCountRef = useRef(0);
  const previousMessageCountRef = useRef(0);

  const notifCounterReadyRef = useRef(false);
  const messageCounterReadyRef = useRef(false);

  const globalMessageAudioRef = useRef(null);
  const globalMessageAudioUnlockedRef = useRef(false);
  const inboxSignalReadyRef = useRef(false);
  const previousIncomingMessageIdRef = useRef("");
  const currentPathRef = useRef(location.pathname);

  // ===== УВЕДОМЛЕНИЯ =====
  useEffect(() => {
    if (!user?.uid) {
      setNotifCount(0);
      return undefined;
    }

    let disposed = false;
    let unsubscribe = null;

    import("../services/headerSummarySubscriptions")
      .then(({ subscribeHeaderNotificationSummary }) => {
        if (disposed) return;

        unsubscribe = subscribeHeaderNotificationSummary(
          user.uid,
          (summary) => {
            if (disposed) return;
            setNotifCount(Math.max(0, Number(summary?.unreadCount || 0)));
          },
          (error) => {
            if (disposed) return;
            console.error("Не удалось загрузить счётчик уведомлений:", error);
            setNotifCount(0);
          }
        );
      })
      .catch((error) => {
        if (disposed) return;
        console.error("Не удалось загрузить модуль счётчика уведомлений:", error);
        setNotifCount(0);
      });

    return () => {
      disposed = true;
      unsubscribe?.();
    };
  }, [user?.uid]);

  // ===== НЕПРОЧИТАННЫЕ И ГЛОБАЛЬНЫЙ СИГНАЛ СООБЩЕНИЙ =====
  useEffect(() => {
    currentPathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    inboxSignalReadyRef.current = false;
    previousIncomingMessageIdRef.current = "";

    if (!user?.uid) {
      setMsgCount(0);
      return undefined;
    }

    /*
     * Один персональный summary-документ даёт:
     * - точный общий unread;
     * - ID последнего входящего сообщения;
     * - chatId и muted-флаг для звука.
     *
     * Header больше не подписывается на 20 inboxDialogs.
     */
    let disposed = false;
    let unsubscribe = null;

    import("../services/headerSummarySubscriptions")
      .then(({ subscribeHeaderInboxSummary }) => {
        if (disposed) return;
        unsubscribe = subscribeHeaderInboxSummary(
          user.uid,
      (summary) => {
        setMsgCount(
          Math.max(
            0,
            Number(summary?.unreadCount || 0)
          )
        );

        const incomingMessageId = String(
          summary?.lastIncomingMessageId || ""
        ).trim();

        if (!inboxSignalReadyRef.current) {
          previousIncomingMessageIdRef.current =
            incomingMessageId;
          inboxSignalReadyRef.current = true;
          return;
        }

        if (
          !incomingMessageId ||
          incomingMessageId ===
            previousIncomingMessageIdRef.current
        ) {
          return;
        }

        previousIncomingMessageIdRef.current =
          incomingMessageId;

        const incomingChatId = String(
          summary?.lastIncomingChatId || ""
        ).trim();

        const activeChatId = getActiveChatId(
          currentPathRef.current
        );

        const chatIsOpenAndVisible = Boolean(
          incomingChatId &&
          activeChatId === incomingChatId &&
          document.visibilityState === "visible"
        );

        if (chatIsOpenAndVisible) {
          return;
        }

        const soundEnabled =
          window.localStorage.getItem(
            "mp_sound_enabled"
          ) !== "false";

        if (
          soundEnabled &&
          summary?.lastIncomingMuted !== true &&
          globalMessageAudioRef.current
        ) {
          globalMessageAudioRef.current.currentTime = 0;
          globalMessageAudioRef.current
            .play()
            .catch(() => {
              // Браузер ждёт первый жест пользователя.
            });
        }
      },
      (error) => {
        console.warn(
          "Не удалось загрузить inbox summary:",
          error
        );
        setMsgCount(0);
      }
        );
      })
      .catch((error) => {
        if (disposed) return;
        console.warn("Не удалось загрузить модуль inbox summary:", error);
        setMsgCount(0);
      });

    return () => {
      disposed = true;
      unsubscribe?.();
    };
  }, [user?.uid]);

  // ===== ЗВУК И РЕГИСТРАЦИЯ УСТРОЙСТВА ДЛЯ PUSH =====
  useEffect(() => {
    const audio = new Audio(
      GLOBAL_MESSAGE_SOUND
    );

    audio.volume = 0.5;
    audio.preload = "auto";
    audio.setAttribute(
      "playsinline",
      ""
    );
    audio.load();

    globalMessageAudioRef.current = audio;
    globalMessageAudioUnlockedRef.current = false;

    return () => {
      audio.pause();

      if (
        globalMessageAudioRef.current === audio
      ) {
        globalMessageAudioRef.current = null;
      }

      globalMessageAudioUnlockedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const removeUnlockListeners = () => {
      document.removeEventListener(
        "pointerdown",
        unlockMessageAudio,
        true
      );
      document.removeEventListener(
        "touchstart",
        unlockMessageAudio,
        true
      );
      document.removeEventListener(
        "keydown",
        unlockMessageAudio,
        true
      );
    };

    const restoreAudio = (
      audio,
      previousMuted,
      previousVolume
    ) => {
      audio.pause();
      audio.currentTime = 0;
      audio.muted = previousMuted;
      audio.volume = previousVolume;
    };

    function unlockMessageAudio() {
      const audio =
        globalMessageAudioRef.current;

      if (
        !audio ||
        globalMessageAudioUnlockedRef.current
      ) {
        return;
      }

      const previousMuted = audio.muted;
      const previousVolume = audio.volume;

      audio.muted = true;
      audio.volume = 0;
      audio.currentTime = 0;

      const playPromise = audio.play();

      if (
        playPromise &&
        typeof playPromise.then === "function"
      ) {
        playPromise
          .then(() => {
            restoreAudio(
              audio,
              previousMuted,
              previousVolume
            );
            globalMessageAudioUnlockedRef.current = true;
            removeUnlockListeners();
          })
          .catch(() => {
            restoreAudio(
              audio,
              previousMuted,
              previousVolume
            );
          });
      } else {
        restoreAudio(
          audio,
          previousMuted,
          previousVolume
        );
        globalMessageAudioUnlockedRef.current = true;
        removeUnlockListeners();
      }
    }

    document.addEventListener(
      "pointerdown",
      unlockMessageAudio,
      true
    );
    document.addEventListener(
      "touchstart",
      unlockMessageAudio,
      {
        passive: true,
        capture: true,
      }
    );
    document.addEventListener(
      "keydown",
      unlockMessageAudio,
      true
    );

    return removeUnlockListeners;
  }, []);

  useEffect(() => {
    if (
      !user?.uid ||
      !("Notification" in window) ||
      Notification.permission !== "granted"
    ) {
      return;
    }

    import("../firebase")
      .then(({ registerMapapetPushDevice }) =>
        registerMapapetPushDevice({
          userId: user.uid,
          requestPermission: false,
        })
      )
      .catch((error) => {
        console.warn(
          "Не удалось обновить push-регистрацию:",
          error
        );
      });
  }, [user?.uid]);

  /*
   * При смене аккаунта начинаем наблюдение заново.
   * Уже существующий счётчик при первой загрузке
   * не должен изображать новое событие.
   */
  useEffect(() => {
    previousNotifCountRef.current = 0;
    previousMessageCountRef.current = 0;

    notifCounterReadyRef.current = false;
    messageCounterReadyRef.current = false;
    inboxSignalReadyRef.current = false;
    previousIncomingMessageIdRef.current = "";

    setNotifPulse(false);
    setMessagePulse(false);
  }, [user?.uid]);

  /*
   * Колокольчик пульсирует только тогда,
   * когда число непрочитанных увеличилось.
   */
  useEffect(() => {
    if (!user?.uid) {
      return undefined;
    }

    if (!notifCounterReadyRef.current) {
      previousNotifCountRef.current = notifCount;
      notifCounterReadyRef.current = true;
      return undefined;
    }

    let animationFrame = null;
    let timer = null;

    if (
      notifCount >
      previousNotifCountRef.current
    ) {
      setNotifPulse(false);

      animationFrame = window.requestAnimationFrame(
        () => {
          setNotifPulse(true);
        }
      );

      timer = window.setTimeout(() => {
        setNotifPulse(false);
      }, 900);
    }

    previousNotifCountRef.current = notifCount;

    return () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(
          animationFrame
        );
      }

      if (timer !== null) {
        window.clearTimeout(timer);
      }
    };
  }, [notifCount, user?.uid]);

  /*
   * Мессенджер получает такую же короткую
   * анимацию при новом непрочитанном сообщении.
   */
  useEffect(() => {
    if (!user?.uid) {
      return undefined;
    }

    if (!messageCounterReadyRef.current) {
      previousMessageCountRef.current = msgCount;
      messageCounterReadyRef.current = true;
      return undefined;
    }

    let animationFrame = null;
    let timer = null;

    if (
      msgCount >
      previousMessageCountRef.current
    ) {
      setMessagePulse(false);

      animationFrame = window.requestAnimationFrame(
        () => {
          setMessagePulse(true);
        }
      );

      timer = window.setTimeout(() => {
        setMessagePulse(false);
      }, 900);
    }

    previousMessageCountRef.current = msgCount;

    return () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(
          animationFrame
        );
      }

      if (timer !== null) {
        window.clearTimeout(timer);
      }
    };
  }, [msgCount, user?.uid]);

  // ===== СИСТЕМА СОБЫТИЙ ДЛЯ МОДАЛОК =====
  useEffect(() => {
    const handler = () =>
      setShowProfileModal(true);

    window.addEventListener(
      "open-profile-dialog",
      handler
    );

    return () =>
      window.removeEventListener(
        "open-profile-dialog",
        handler
      );
  }, []);

  useEffect(() => {
    const handler = () =>
      setShowNotifications(true);

    window.addEventListener(
      "open-notifications",
      handler
    );

    return () =>
      window.removeEventListener(
        "open-notifications",
        handler
      );
  }, []);

  useEffect(() => {
    const handler = () =>
      setShowForum(true);

    window.addEventListener(
      "open-forum",
      handler
    );

    return () =>
      window.removeEventListener(
        "open-forum",
        handler
      );
  }, []);

  useEffect(() => {
    const handler = () =>
      navigate("/messages");

    window.addEventListener(
      "open-messages",
      handler
    );

    return () =>
      window.removeEventListener(
        "open-messages",
        handler
      );
  }, [navigate]);

  useEffect(() => {
    const handler = () =>
      setShowBookmarks(true);

    window.addEventListener(
      "open-bookmarks",
      handler
    );

    return () =>
      window.removeEventListener(
        "open-bookmarks",
        handler
      );
  }, []);

  useEffect(() => {
    const handler = (event) => {
      window.dispatchEvent(
        new CustomEvent(
          "open-reviews",
          {
            detail: event.detail,
          }
        )
      );
    };

    window.addEventListener(
      "open-review-reply",
      handler
    );

    return () =>
      window.removeEventListener(
        "open-review-reply",
        handler
      );
  }, []);

  // ===== ДЕНЬ / НОЧЬ =====
  useEffect(() => {
    const scopedTheme = getInitialTheme(
      themeScope
    );

    setDarkMode(scopedTheme === "dark");
    applyTheme(scopedTheme);
  }, [themeScope]);

  useEffect(() => {
    applyTheme(
      darkMode ? "dark" : "light"
    );
  }, [darkMode]);

  // Глобальный вызов модалки логина.
  useEffect(() => {
    window.showLoginModal = () =>
      setShowAuthModal(true);
  }, []);

  useEffect(() => {
    if (!user?.uid) return;

    let pending = null;

    try {
      const raw = window.sessionStorage.getItem(
        'mapapet-pending-listing-placement'
      );

      if (!raw) return;

      pending = JSON.parse(raw);
      window.sessionStorage.removeItem(
        'mapapet-pending-listing-placement'
      );
    } catch (error) {
      console.warn('Could not restore pending listing placement:', error);
      return;
    }

    const createdAt = Number(pending?.createdAt || 0);
    const isFresh =
      createdAt > 0 && Date.now() - createdAt < 30 * 60 * 1000;

    if (!isFresh) return;

    setShowAuthModal(false);
    navigate('/user-map', {
      state: {
        startPlacement: true,
        category: pending?.category || 'dogs',
        subCategory: pending?.subCategory || '',
      },
    });
  }, [navigate, user?.uid]);

  // ===== ХЭНДЛЕРЫ =====
  const handleLogout = async () => {
    try {
      const [{ auth }, { signOut }] = await Promise.all([
        import("../firebaseAuthClient"),
        import("firebase/auth"),
      ]);
      await signOut(auth);
    } catch (error) {
      console.error("Не удалось выйти из аккаунта:", error);
    } finally {
      handleMenuClose();
    }
  };

  const handleMenuOpen = (event) =>
    setAnchorEl(event.currentTarget);

  const handleMenuClose = () =>
    setAnchorEl(null);

  const handleLangOpen = (event) =>
    setLangAnchor(event.currentTarget);

  const handleLangClose = () =>
    setLangAnchor(null);

  const handleOpenProfile = () => {
    window.dispatchEvent(
      new CustomEvent(
        "open-profile-dialog"
      )
    );

    handleMenuClose();
  };

  const avatarUrl =
    user?.avatar ||
    "/img/default-avatar.svg";

  const languages = [
    {
      code: "en",
      name: "English",
      ready: true,
    },
    {
      code: "uk",
      name: "Українська",
      ready: true,
    },
    {
      code: "de",
      name: "Deutsch",
      ready: true,
    },
    {
      code: "fr",
      name: "Français",
      ready: true,
    },
    {
      code: "es",
      name: "Español",
      ready: true,
    },
    {
      code: "pt",
      name: "Português",
      ready: true,
    },
    {
      code: "it",
      name: "Italiano",
      ready: true,
    },
    {
      code: "pl",
      name: "Polski",
      ready: true,
    },
    {
      code: "tr",
      name: "Türkçe",
      ready: true,
    },
    {
      code: "id",
      name: "Bahasa Indonesia",
      ready: true,
    },
    {
      code: "hi",
      name: "हिन्दी",
      ready: true,
    },
    {
      code: "ko",
      name: "한국어",
      ready: true,
    },
    {
      code: "ja",
      name: "日本語",
      ready: true,
    },
    {
      code: "zh",
      name: "中文",
      ready: true,
    },
    {
      code: "ar",
      name: "العربية",
      ready: true,
    },
    {
      code: "ru",
      name: "Русский",
      ready: true,
    },
  ];

  const handleLanguageChange = async (code) => {
    handleLangClose();

    const selectedLanguage = languages.find(
      (language) => language.code === code
    );

    if (!selectedLanguage?.ready) {
      window.alert(
        t("header.translationComingSoon", {
          language: selectedLanguage?.name || code,
        })
      );
      return;
    }

    const {
      hostname,
      pathname,
      search,
      hash,
    } = window.location;

    const normalizedHost = hostname.toLowerCase();

    const targetHosts = {
      en: "mapapet.com",
      ru: "ru.mapapet.com",
      de: "de.mapapet.com",
      uk: "uk.mapapet.com",
      es: "es.mapapet.com",
      pt: "pt.mapapet.com",
      fr: "fr.mapapet.com",
      it: "it.mapapet.com",
      pl: "pl.mapapet.com",
      tr: "tr.mapapet.com",
      id: "id.mapapet.com",
      hi: "hi.mapapet.com",
      ko: "ko.mapapet.com",
      ja: "ja.mapapet.com",
      zh: "zh.mapapet.com",
      ar: "ar.mapapet.com",
    };

    const targetHost = targetHosts[code] || "mapapet.com";

    const isMapapetProductionHost =
      normalizedHost === "mapapet.com" ||
      normalizedHost === "www.mapapet.com" ||
      normalizedHost.endsWith(".mapapet.com");

    /*
     * Localhost / Firebase preview:
     * there is no language subdomain, so change i18n in-place.
     */
    if (!isMapapetProductionHost) {
      window.localStorage.setItem("mapapet-language", code);
      await i18n.changeLanguage(code);
      return;
    }

    /*
     * Production:
     * hostname is the source of truth for language.
     * Do NOT call i18n.changeLanguage() before cross-domain navigation.
     * Doing both caused a visible language flash and occasional stale state
     * while the destination locale chunk was still loading.
     */
    const alreadyOnTargetHost =
      normalizedHost === targetHost ||
      (code === "en" && normalizedHost === "www.mapapet.com");

    if (alreadyOnTargetHost) {
      window.localStorage.setItem("mapapet-language", code);

      if (
        (i18n.resolvedLanguage || i18n.language) !== code
      ) {
        await i18n.changeLanguage(code);
      }
      return;
    }

    let targetPathname = pathname;
    let targetSearch = search;
    let targetHash = hash;

    /*
     * Language-exclusive blog posts exist only on one locale.
     * If we preserve their slug while switching to another language,
     * BlogPostPage correctly redirects back to the original locale.
     * To a user that looks like a broken language switch.
     *
     * When the destination language has no version of that article,
     * open the destination blog index instead.
     */
    const blogPostMatch = pathname.match(
      /^\/blog\/([^/]+)\/?$/
    );

    if (blogPostMatch) {
      let currentSlug = blogPostMatch[1];

      try {
        currentSlug = decodeURIComponent(currentSlug);
      } catch {
        // Keep the raw slug if the path contains malformed escaping.
      }

      const exclusiveLanguage =
        getExclusiveBlogLanguage(currentSlug);

      if (
        exclusiveLanguage &&
        exclusiveLanguage !== code
      ) {
        targetPathname = "/blog";
        targetSearch = "";
        targetHash = "";
      }
    }

    window.location.assign(
      `https://${targetHost}` +
        `${targetPathname}${targetSearch}${targetHash}`
    );
  };

  const handleOpenMessages = () => {
    if (user?.uid) {
      import("../firebase")
        .then(({ registerMapapetPushDevice }) =>
          registerMapapetPushDevice({
            userId: user.uid,
            requestPermission: true,
          })
        )
        .catch((error) => {
          console.warn(
            "Push-уведомления пока не подключились:",
            error
          );
        });
    }

    navigate("/messages");
  };

  const handleMapClick = () => {
    if (!user) {
      setMapNoticeOpen(true);
      return;
    }

    navigate("/user-map");
  };

  const mapIconSrc = user
    ? "/img/map-icon-active.png"
    : "/img/map-icon-inactive.png";

  const handleToggleDarkMode = () => {
    setDarkMode((previous) => {
      const nextTheme = previous
        ? "light"
        : "dark";

      saveThemePreference(
        nextTheme,
        themeScope
      );

      return nextTheme === "dark";
    });
  };

  const isAdmin =
    role &&
    (
      role === "superadmin" ||
      role === "admin"
    );

  const isOnUserMap =
    location.pathname ===
    "/user-map";

  const mapButtonHint = user
    ? t(
        isOnUserMap
          ? "header.mapMyAdsHint"
          : "header.mapCreateAdHint",
        {
          defaultValue: isOnUserMap
            ? "Моя карта заявок"
            : "Оставить заявку",
        }
      )
    : t(
        "header.mapRegistrationRequired",
        {
          defaultValue:
            "Карта заявок доступна только после регистрации.",
        }
      );

  // ===== RENDER =====
  return (
    <>
      <header className="header">
        <HeaderLogo />

        <div className="google-search desktop-only">
          <DeferredGoogleSearch
            className="header-deferred-search--desktop"
          />
        </div>

        <nav
          className="nav"
          style={{
            marginLeft: "12px",
          }}
        >
          {/* Ряд 1: Главная / Блог / Форум */}
          <div className="nav-main">
            <NavLink
              to="/"
              className="nav-link"
            >
              {t("header.home")}
            </NavLink>

            <NavLink
              to="/blog"
              className="nav-link"
            >
              {t("header.blog")}
            </NavLink>

            <NavLink
              to="/forum"
              className="nav-link"
            >
              {t("header.forum")}
            </NavLink>
          </div>

          {/* Ряд 2: карта + уведомления + аватар + языки */}
          <div className="nav-actions">
            {!user ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Tooltip
                  title={mapButtonHint}
                  arrow
                  enterDelay={250}
                >
                  <button
                    type="button"
                    className={
                      `nav-link map-link ` +
                      `map-link--guest ` +
                      `header-action-btn ` +
                      `header-map-btn ${
                        isOnUserMap
                          ? "map-link--active"
                          : ""
                      }`
                    }
                    onClick={handleMapClick}
                    aria-label={mapButtonHint}
                    title={mapButtonHint}
                  >
                    <img
                      src={mapIconSrc}
                      alt={t(
                        "common.map"
                      )}
                      className="map-link-icon"
                    />
                  </button>
                </Tooltip>

                <button
                  type="button"
                  className="nav-link"
                  onClick={() =>
                    setShowAuthModal(
                      true
                    )
                  }
                >
                  {t(
                    "header.register"
                  )}
                </button>

                <Tooltip
                  title={t(
                    "header.language"
                  )}
                >
                  <IconButton
                    onClick={
                      handleLangOpen
                    }
                    className="desktop-lang-btn"
                  >
                    <TranslateIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Tooltip
                  title={mapButtonHint}
                  arrow
                  enterDelay={250}
                >
                  <button
                    type="button"
                    className={
                      `nav-link map-link ` +
                      `header-action-btn ` +
                      `header-map-btn ${
                        isOnUserMap
                          ? "map-link--active"
                          : ""
                      }`
                    }
                    onClick={handleMapClick}
                    aria-label={mapButtonHint}
                    title={mapButtonHint}
                  >
                    <img
                      src={mapIconSrc}
                      alt={t(
                        "common.map"
                      )}
                      className="map-link-icon"
                    />
                  </button>
                </Tooltip>

                <IconButton
                  onClick={() =>
                    setShowNotifications(
                      true
                    )
                  }
                  className={
                    `header-action-btn ` +
                    `header-notification-btn ${
                      notifPulse
                        ? "is-pulsing"
                        : ""
                    } ${
                      notifCount > 0
                        ? "has-unread"
                        : ""
                    }`
                  }
                  title={t(
                    "header.notifications",
                    {
                      defaultValue:
                        "Уведомления",
                    }
                  )}
                  aria-label={t(
                    "header.notifications",
                    {
                      defaultValue:
                        "Уведомления",
                    }
                  )}
                >
                  <Badge
                    badgeContent={
                      notifCount
                    }
                    max={99}
                    color="error"
                    className="header-action-badge"
                  >
                    <NotificationsIcon className="header-action-icon" />
                  </Badge>
                </IconButton>

                <IconButton
                  onClick={
                    handleOpenMessages
                  }
                  className={
                    `header-action-btn ` +
                    `header-message-btn ${
                      messagePulse
                        ? "is-pulsing"
                        : ""
                    } ${
                      msgCount > 0
                        ? "has-unread"
                        : ""
                    }`
                  }
                  title={t(
                    "header.messages"
                  )}
                  aria-label={t(
                    "header.messages"
                  )}
                >
                  <Badge
                    badgeContent={
                      msgCount
                    }
                    max={99}
                    color="error"
                    className="header-action-badge"
                  >
                    <MessageIcon className="header-action-icon" />
                  </Badge>

                  <span
                    className="message-activity-dots"
                    aria-hidden="true"
                  >
                    <i />
                    <i />
                    <i />
                  </span>
                </IconButton>

                <Tooltip
                  title={t(
                    "header.profile"
                  )}
                >
                  <IconButton
                    onClick={
                      handleMenuOpen
                    }
                    sx={{
                      p: 0,
                    }}
                  >
                    <Avatar
                      src={avatarUrl}
                      alt="avatar"
                      sx={{
                        width: 36,
                        height: 36,
                      }}
                    />
                  </IconButton>
                </Tooltip>

                <Tooltip
                  title={t(
                    "header.language"
                  )}
                >
                  <IconButton
                    onClick={
                      handleLangOpen
                    }
                    className="desktop-lang-btn"
                  >
                    <TranslateIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {/* Меню выбора языка */}
            <Menu
              anchorEl={langAnchor}
              open={
                Boolean(langAnchor) &&
                window.innerWidth >= 768
              }
              onClose={handleLangClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              {languages.map(
                ({
                  code,
                  name,
                  ready,
                }) => (
                  <MenuItem
                    key={code}
                    selected={
                      i18n.resolvedLanguage ===
                      code
                    }
                    onClick={() =>
                      handleLanguageChange(
                        code
                      )
                    }
                    sx={{
                      opacity:
                        ready
                          ? 1
                          : 0.62,
                    }}
                  >
                    <img
                      src={
                        `/flags/${code}.png`
                      }
                      alt={code}
                      width="20"
                      height="15"
                      style={{
                        marginRight:
                          "10px",
                        borderRadius:
                          "2px",
                      }}
                    />

                    {name}

                    {!ready && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          ml: 1,
                          opacity: 0.7,
                        }}
                      >
                        {t(
                          "header.soon"
                        )}
                      </Typography>
                    )}
                  </MenuItem>
                )
              )}
            </Menu>

            {/* Меню профиля */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              {user && (
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                  }}
                >
                  <Typography variant="subtitle2">
                    {user.email}
                  </Typography>
                </Box>
              )}

              <Divider />

              <MenuItem
                onClick={
                  handleOpenProfile
                }
              >
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>

                {t("header.profile")}
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setShowMyApps(true);
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <Dashboard fontSize="small" />
                </ListItemIcon>

                {t("header.myAds")}
              </MenuItem>

              <MenuItem
                onClick={() => {
                  window.dispatchEvent(
                    new CustomEvent(
                      "open-bookmarks"
                    )
                  );

                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <BookmarkIcon fontSize="small" />
                </ListItemIcon>

                {t(
                  "header.bookmarks"
                )}
              </MenuItem>

              <MenuItem
                onClick={() => {
                  handleOpenMessages();
                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <MessageIcon fontSize="small" />
                </ListItemIcon>

                {t(
                  "header.messages"
                )}
              </MenuItem>

              <MenuItem
                onClick={() => {
                  window.dispatchEvent(
                    new CustomEvent(
                      "open-forum"
                    )
                  );

                  handleMenuClose();
                }}
              >
                <ListItemIcon>
                  <ForumIcon fontSize="small" />
                </ListItemIcon>

                {t("header.forum")}
              </MenuItem>

              {isAdmin && (
                <MenuItem
                  onClick={() => {
                    navigate("/admin");
                    handleMenuClose();
                  }}
                >
                  <DashboardIcon
                    sx={{
                      mr: 1,
                    }}
                  />

                  {t("header.admin")}
                </MenuItem>
              )}

              {role === "moderator" && (
                <MenuItem
                  onClick={() =>
                    navigate(
                      "/moderation"
                    )
                  }
                >
                  <SecurityIcon
                    sx={{
                      mr: 1,
                    }}
                  />

                  {t(
                    "header.moderation"
                  )}
                </MenuItem>
              )}

              <Divider />

              <MenuItem
                onClick={
                  handleToggleDarkMode
                }
              >
                <ListItemIcon>
                  {darkMode ? (
                    <WbSunnyIcon fontSize="small" />
                  ) : (
                    <NightlightIcon fontSize="small" />
                  )}
                </ListItemIcon>

                <Switch
                  checked={darkMode}
                  onClick={(event) =>
                    event.stopPropagation()
                  }
                  onChange={
                    handleToggleDarkMode
                  }
                  size="small"
                  inputProps={{
                    "aria-label": t(
                      "header.theme"
                    ),
                  }}
                />

                {t("header.theme")}
              </MenuItem>

              <MenuItem
                onClick={handleLogout}
              >
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>

                {t("header.logout")}
              </MenuItem>
            </Menu>
          </div>
        </nav>
      </header>

      {/* Мобильная строка: видео + поиск + язык */}
      <div className="mobile-header-row">
        <div className="mobile-video">
          <img
            src="/img/video-button.png"
            alt={t(
              "common.video"
            )}
            className="video-button-circle"
          />
        </div>

        <div className="mobile-google-search">
          <DeferredGoogleSearch
            className="header-deferred-search--mobile"
          />
        </div>

        <div className="mobile-languages">
          <Tooltip
            title={t(
              "header.language"
            )}
          >
            <IconButton
              onClick={
                handleLangOpen
              }
            >
              <TranslateIcon />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={langAnchor}
            open={
              Boolean(langAnchor) &&
              window.innerWidth < 768
            }
            onClose={handleLangClose}
          >
            {languages.map(
              ({
                code,
                name,
                ready,
              }) => (
                <MenuItem
                  key={code}
                  selected={
                    i18n.resolvedLanguage ===
                    code
                  }
                  onClick={() =>
                    handleLanguageChange(
                      code
                    )
                  }
                  sx={{
                    opacity:
                      ready
                        ? 1
                        : 0.62,
                  }}
                >
                  <img
                    src={
                      `/flags/${code}.png`
                    }
                    alt={code}
                    width="20"
                    height="15"
                    style={{
                      marginRight:
                        "10px",
                      borderRadius:
                        "2px",
                    }}
                  />

                  {name}

                  {!ready && (
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        ml: 1,
                        opacity: 0.7,
                      }}
                    >
                      {t(
                        "header.soon"
                      )}
                    </Typography>
                  )}
                </MenuItem>
              )
            )}
          </Menu>
        </div>
      </div>

      <Snackbar
        open={mapNoticeOpen}
        autoHideDuration={3200}
        onClose={(_, reason) => {
          if (reason !== "clickaway") {
            setMapNoticeOpen(false);
          }
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={() =>
            setMapNoticeOpen(false)
          }
          sx={{ width: "100%" }}
        >
          {t(
            "header.mapRegistrationRequired",
            {
              defaultValue:
                "Карта заявок доступна только после регистрации.",
            }
          )}
        </Alert>
      </Snackbar>

      <Suspense fallback={null}>
        {showAuthModal && (
          <AuthModal
            onClose={() =>
              setShowAuthModal(
                false
              )
            }
          />
        )}

        {showMyApps && (
          <MyApplicationsModal
            onClose={() =>
              setShowMyApps(false)
            }
          />
        )}

        {showProfileModal && (
          <ProfileModal
            open={
              showProfileModal
            }
            onClose={() =>
              setShowProfileModal(
                false
              )
            }
          />
        )}

        {showBookmarks && (
          <BookmarksModal
            open={showBookmarks}
            onClose={() =>
              setShowBookmarks(
                false
              )
            }
          />
        )}

        {showForum && (
          <ForumModal
            open={showForum}
            onClose={() =>
              setShowForum(false)
            }
          />
        )}

        {showNotifications && (
          <NotificationsModal
            isOpen={
              showNotifications
            }
            onClose={() =>
              setShowNotifications(
                false
              )
            }
          />
        )}
      </Suspense>
    </>
  );
};

export default Header;